import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import config from '../../config/config';
import "../../styles/TriggerMonitoring.css";

const TOTAL_PENDING_TIME = 7200; // 2 hours

const TriggerMonitoring = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessful, setShowSuccessful] = useState(false);
  const [showStopped, setShowStopped] = useState(false);
  const [pendingTimers, setPendingTimers] = useState({});
  const [showEndingSoon, setShowEndingSoon] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [stoppedPendingJobs, setStoppedPendingJobs] = useState(new Set());

  // ▼▼ NEW: Info modal state & helpers ▼▼
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalText, setInfoModalText] = useState("");
  const openInfoModal = (text) => {
    setInfoModalText(text || "");
    setInfoModalOpen(true);
  };
  const closeInfoModal = () => setInfoModalOpen(false);
  const copyInfoToClipboard = async () => {
    try { await navigator.clipboard.writeText(infoModalText || ""); } catch(_) {}
  };
  const renderInfoPreview = (text, fallback = "No Info") => {
    const value = (text && String(text)) || fallback;
    const max = 120; // ekranda gösterilecek karakter sayısı
    const isLong = value.length > max;
    return isLong ? (
      <>
        <span className="info-preview" title={value}>{value.slice(0, max)}…</span>
        <button type="button" className="link-button" onClick={() => openInfoModal(value)}>View</button>
      </>
    ) : (
      <span className="info-preview" title={value}>{value}</span>
    );
  };
  // ▲▲ NEW ▲▲

  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem("userToken");
      const selectedTenant = JSON.parse(localStorage.getItem("selectedTenant"));

      const tenantId = selectedTenant?.orch_tenant_id || selectedTenant?.id;
      if (!tenantId) {
        setJobs([]);
        setLoading(false);
        return;
      }

      const resp = await axios.get(
        `${config.API_BASE_URL}/api/triggerMonitoring`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            days: 150,
            tenantId, // 🔑 backend bu paramı istiyor
          },
          timeout: 30000,
        }
      );

      const transformed = (resp.data.jobs || []).map((job) => ({
        ...job,
        hostname: job.hostname === "N/A" ? "Not Available" : (job.hostname || "Not Available"),
      }));

      setJobs(transformed);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching jobs:", error.response?.data || error.message);
      setJobs([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const storedTenant = localStorage.getItem("selectedTenant");
      if (storedTenant) {
        const parsed = JSON.parse(storedTenant);
        if (parsed?.orch_tenant_id || parsed?.id) {
          fetchJobs();
          const interval = setInterval(fetchJobs, 5000);
          return () => clearInterval(interval);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error handling selectedTenant:", error);
      setLoading(false);
    }
  }, [fetchJobs]);

  const sortedJobs = useMemo(() => {
    return Array.isArray(jobs)
      ? [...jobs].sort((a, b) => {
          const order = { Running: 1, Pending: 2, Successful: 3, Stopped: 4, Disabled: 5 };
          return (order[a.state] || 99) - (order[b.state] || 99);
        })
      : [];
  }, [jobs]);

  const parseCreationTime = (creationTime) => {
    if (!creationTime) return null;
    const d = new Date(creationTime);
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return null;
    const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${secs}`;
  };

  const groupJobsByDate = (jobs) => {
    return jobs.reduce((groups, job) => {
      if (!job.creationTime) return groups;
      const date = new Date(job.creationTime);
      const key = date.toISOString().split("T")[0];
      if (!groups[key]) groups[key] = [];
      groups[key].push(job);
      return groups;
    }, {});
  };

  const toggleDate = (date) => {
    setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  useEffect(() => {
    const saved = localStorage.getItem("stoppedPendingJobs");
    if (saved) {
      setStoppedPendingJobs(new Set(JSON.parse(saved)));
    }
  }, []);

  const stopJob = useCallback(
    async (jobId, folderId) => {
      try {
        const token = localStorage.getItem("userToken");
        const selectedTenant = JSON.parse(localStorage.getItem("selectedTenant"));
        const tenantId = selectedTenant?.orch_tenant_id || selectedTenant?.id;

        if (!tenantId) {
          console.error("No tenantId found");
          return;
        }

        const resp = await axios.post(
          `${config.API_BASE_URL}/api/triggerMonitoring/jobs/${jobId}/stop`,
          {
            folderId,
            tenantId, // 🔑 backend bu paramı istiyor
            jobInfo: "Pending Disabled (Timeout)",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            timeout: 30000,
          }
        );

        if (resp.status === 200 || resp.status === 204) {
          setStoppedPendingJobs((prev) => {
            const next = new Set([...prev, jobId]);
            localStorage.setItem("stoppedPendingJobs", JSON.stringify([...next]));
            return next;
          });
          await fetchJobs();
        }
      } catch (error) {
        console.error("Stop job error:", {
          status: error.response?.status,
          data: error.response?.data,
          jobId,
          folderId,
        });

        // 404 ise zaten bitmiş olabilir; UI’ı yine güncelle
        if (error.response?.status === 404) {
          setStoppedPendingJobs((prev) => {
            const next = new Set([...prev, jobId]);
            localStorage.setItem("stoppedPendingJobs", JSON.stringify([...next]));
            return next;
          });
        }

        await fetchJobs();
      }
    },
    [fetchJobs]
  );

  // Pending sayaçları ve otomatik stop
  useEffect(() => {
    if (!jobs.length) return;

    const updatePendingTimers = () => {
      const newTimers = {};
      const newBlink = {};
      const stopped = new Set();

      sortedJobs
        .filter((j) => j.state === "Pending")
        .forEach((job) => {
          const creationStr = parseCreationTime(job.creationTime);
          if (!creationStr) return;

          const [hh, mm, ss] = creationStr.split(":").map(Number);
          const creationSeconds = hh * 3600 + mm * 60 + ss;

          const now = new Date();
          const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

          const elapsed = nowSec - creationSeconds;
          const remaining = Math.max(TOTAL_PENDING_TIME - elapsed, 0);

          if (remaining === 0 && !job.info?.includes("Disabled Automatically")) {
            if (!stopped.has(job.id)) {
              stopped.add(job.id);
              stopJob(job.id, job.folderId).catch(console.error);
            }
          }

          newTimers[job.id] = remaining;
          if (remaining <= 60 && remaining > 0) newBlink[job.id] = true;
        });

      const currTimers = JSON.stringify(pendingTimers);
      const nextTimers = JSON.stringify(newTimers);
      if (currTimers !== nextTimers) setPendingTimers(newTimers);

      const currBlink = JSON.stringify(showEndingSoon);
      const nextBlink = JSON.stringify(newBlink);
      if (currBlink !== nextBlink) setShowEndingSoon(newBlink);
    };

    updatePendingTimers();
    const interval = setInterval(updatePendingTimers, 5000);
    return () => clearInterval(interval);
  }, [jobs, stopJob]); // eslint-disable-line

  // EndingSoon blink
  useEffect(() => {
    const interval = setInterval(() => {
      setShowEndingSoon((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          next[id] = !next[id];
        });
        return next;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="trigger-monitoring-container">
      <Helmet>
        <title>Trigger Monitoring - Autonomie</title>
      </Helmet>

      <h2>Trigger Monitoring</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="trigger-monitoring-table">
          <thead>
            <tr>
              <th>Process</th>
              <th>Folder</th>
              <th>Hostname</th>
              <th>State</th>
              <th>Creation Time</th>
              <th>Started Time</th>
              <th>Ended Time</th>
              <th>Duration</th>
              <th>INFO</th>
            </tr>
          </thead>
          <tbody>
            {/* Running & Pending */}
            {sortedJobs.some((j) => j.state === "Running" || j.state === "Pending") ? (
              sortedJobs
                .filter((j) => j.state === "Running" || j.state === "Pending")
                .sort((a, b) => new Date(b.creationTime) - new Date(a.creationTime))
                .map((job) => (
                  <tr key={job.id}>
                    <td>{job.process}</td>
                    <td>{job.folderName}</td>
                    <td>{job.hostname || "Not Available"}</td>
                    <td
                      className={`job-status ${
                        job.state === "Running"
                          ? "flashing-running"
                          : job.state === "Pending"
                          ? "flashing-pending"
                          : ""
                      }`}
                    >
                      {job.state}
                    </td>
                    <td>
                      {job.creationTime
                        ? new Date(job.creationTime).toLocaleString("tr-TR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })
                        : "Pending"}
                    </td>
                    <td>
                      {job.startTime
                        ? new Date(job.startTime).toLocaleString("tr-TR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })
                        : job.state === "Pending"
                        ? "Not Started"
                        : "In Progress"}
                    </td>
                    <td>
                      {job.endTime
                        ? new Date(job.endTime).toLocaleString("tr-TR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })
                        : job.state === "Running"
                        ? "In Progress"
                        : job.state === "Pending"
                        ? "Not Completed"
                        : "Not Available"}
                    </td>
                    <td>{job.duration || (job.state === "Running" ? "Calculating..." : "Not Available")}</td>
                    <td className="info-cell">
                      {job.state === "Pending" ? (
                        pendingTimers[job.id] === 0 ? (
                          <span className="disabled-automatically">Disabled Automatically</span>
                        ) : (
                          <span className="pending-timer">
                            {pendingTimers[job.id] <= 60 && showEndingSoon[job.id]
                              ? "Ending Soon"
                              : (pendingTimers[job.id] != null
                                  ? `${formatTime(pendingTimers[job.id])}`
                                  : "Pending")}
                          </span>
                        )
                      ) : (
                        // kısalt + modal
                        renderInfoPreview(
                          (stoppedPendingJobs.has(job.id) && "Pending Disabled (Timeout)") || job.info,
                          "No Info"
                        )
                      )}
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan="9" className="no-jobs-message">
                  No running or pending state jobs
                </td>
              </tr>
            )}

            {/* Successful */}
            {sortedJobs.some((j) => j.state === "Successful") && (
              <>
                <tr
                  className="collapsible-header"
                  onClick={() => setShowSuccessful(!showSuccessful)}
                >
                  <td colSpan="9">
                    🔵 Successful ({sortedJobs.filter((j) => j.state === "Successful").length}){" "}
                    {showSuccessful ? "▼" : "▶"}
                  </td>
                </tr>
                {showSuccessful &&
                  Object.entries(
                    groupJobsByDate(sortedJobs.filter((j) => j.state === "Successful"))
                  )
                    .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                    .map(([date, items]) => (
                      <React.Fragment key={date}>
                        <tr className="sub-collapsible-header" onClick={() => toggleDate(date)}>
                          <td colSpan="9">
                            📅{" "}
                            {new Date(date).toLocaleDateString("tr-TR", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                            {expandedDates[date] ? " ▼" : " ▶"}
                          </td>
                        </tr>
                        {expandedDates[date] &&
                          items.map((job) => (
                            <tr key={job.id} className="sub-collapsible-job">
                              <td>{job.process}</td>
                              <td>{job.folderName}</td>
                              <td>{job.hostname || "Not Available"}</td>
                              <td className={job.state.toLowerCase()}>{job.state}</td>
                              <td>
                                {job.creationTime
                                  ? new Date(job.creationTime).toLocaleString("tr-TR")
                                  : "Pending"}
                              </td>
                              <td>
                                {job.startTime
                                  ? new Date(job.startTime).toLocaleString("tr-TR")
                                  : job.state === "Pending"
                                  ? "Not Started"
                                  : "In Progress"}
                              </td>
                              <td>
                                {job.endTime
                                  ? new Date(job.endTime).toLocaleString("tr-TR")
                                  : job.state === "Running"
                                  ? "In Progress"
                                  : job.state === "Pending"
                                  ? "Not Completed"
                                  : "Not Available"}
                              </td>
                              <td>{job.duration || "Not Available"}</td>
                              <td className="info-cell">
                                {renderInfoPreview(job.info, "No Info")}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
              </>
            )}

            {/* Stopped */}
            {sortedJobs.some((j) => j.state === "Stopped") && (
              <>
                <tr
                  className="collapsible-header stopped"
                  onClick={() => setShowStopped(!showStopped)}
                >
                  <td colSpan="9">
                    🔴 Stopped ({sortedJobs.filter((j) => j.state === "Stopped").length}){" "}
                    {showStopped ? "▼" : "▶"}
                  </td>
                </tr>
                {showStopped &&
                  Object.entries(
                    groupJobsByDate(sortedJobs.filter((j) => j.state === "Stopped"))
                  )
                    .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                    .map(([date, items]) => (
                      <React.Fragment key={date}>
                        <tr className="sub-collapsible-header" onClick={() => toggleDate(date)}>
                          <td colSpan="9">
                            📅{" "}
                            {new Date(date).toLocaleDateString("tr-TR", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                            {expandedDates[date] ? " ▼" : " ▶"}
                          </td>
                        </tr>

                        {expandedDates[date] &&
                          items.map((job) => (
                            <tr key={job.id} className="sub-collapsible-job">
                              <td>{job.process}</td>
                              <td>{job.folderName}</td>
                              <td>{job.hostname || "Not Available"}</td>
                              <td className={job.state.toLowerCase()}>{job.state}</td>
                              <td>
                                {job.creationTime
                                  ? new Date(job.creationTime).toLocaleString("tr-TR")
                                  : "Pending"}
                              </td>
                              <td>
                                {job.startTime
                                  ? new Date(job.startTime).toLocaleString("tr-TR")
                                  : job.state === "Pending"
                                  ? "Not Started"
                                  : "In Progress"}
                              </td>
                              <td>
                                {job.endTime
                                  ? new Date(job.endTime).toLocaleString("tr-TR")
                                  : job.state === "Running"
                                  ? "In Progress"
                                  : job.state === "Pending"
                                  ? "Not Completed"
                                  : "Not Available"}
                              </td>
                              <td>{job.duration || "Not Available"}</td>
                              <td className="info-cell">
                                {renderInfoPreview(
                                  stoppedPendingJobs.has(job.id)
                                    ? "Pending Disabled (Timeout)"
                                    : job.info,
                                  "No Info"
                                )}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
              </>
            )}
          </tbody>
        </table>
      )}

      {/* ▼▼ NEW: Info Modal ▼▼ */}
      {infoModalOpen && (
        <div className="modal-backdrop" onClick={closeInfoModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Job Info</h3>
              <button className="modal-close" onClick={closeInfoModal}>×</button>
            </div>
            <div className="modal-body">
              <pre className="modal-pre">{infoModalText}</pre>
            </div>
            <div className="modal-footer">
              <button className="secondary" onClick={copyInfoToClipboard}>Copy</button>
              <button className="primary" onClick={closeInfoModal}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ▲▲ NEW ▲▲ */}
    </div>
  );
};

export default TriggerMonitoring;



// YENI TOKEN ALMADAN ONCEKI KOD - DEGISTIRILME TARIHI 29.08.2025
// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import { Helmet } from "react-helmet";
// import axios from "axios";
// import config from '../../config/config';
// import "../../styles/TriggerMonitoring.css";

// const TOTAL_PENDING_TIME = 7200; // 2 hours in seconds

// const TriggerMonitoring = () => {
//   // State declarations first
//   const [jobs, setJobs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showSuccessful, setShowSuccessful] = useState(false);
//   const [showStopped, setShowStopped] = useState(false);
//   // const [showDisabled, setShowDisabled] = useState(false);
//   const [pendingTimers, setPendingTimers] = useState({});
//   const [showEndingSoon, setShowEndingSoon] = useState({});
//   const [expandedDates, setExpandedDates] = useState({});
//   const [stoppedPendingJobs, setStoppedPendingJobs] = useState(new Set());

//   // Define fetchJobs before using it in useEffect
//   const fetchJobs = useCallback(async () => {
//     try {
//       const token = localStorage.getItem("userToken");
//       const selectedTenant = JSON.parse(localStorage.getItem("selectedTenant"));

//       if (!selectedTenant?.orch_tenant_id) {
//         setJobs([]);
//         setLoading(false);
//         return;
//       }

//       const response = await axios.get(`${config.API_BASE_URL}/api/triggerMonitoring?days=150`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           'X-UIPATH-OrganizationUnitId': selectedTenant.orch_tenant_id.toString(),
//           'Content-Type': 'application/json',
//           'selected-tenant': JSON.stringify(selectedTenant)
//         },
//       });

//       // Transform the jobs data to replace "N/A" with "Not Available"
//       const transformedJobs = (response.data.jobs || []).map(job => ({
//         ...job,
//         hostname: job.hostname === "N/A" ? "Not Available" : job.hostname || "Not Available"
//       }));

//       setJobs(transformedJobs);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching jobs:", error.response?.data || error.message);
//       setJobs([]);
//       setLoading(false);
//     }
//   }, []);

//   // Now we can use fetchJobs in useEffect
//   useEffect(() => {
//     try {
//       const storedTenant = localStorage.getItem("selectedTenant");
//       if (storedTenant) {
//         const parsedTenant = JSON.parse(storedTenant);
//         if (parsedTenant?.orch_tenant_id) {
//           fetchJobs();
//           const interval = setInterval(fetchJobs, 5000);
//           return () => clearInterval(interval);
//         }
//       }
//       setLoading(false);
//     } catch (error) {
//       console.error("Error handling selectedTenant:", error);
//       setLoading(false);
//     }
//   }, [fetchJobs]);

//   // Then define sortedJobs
//   const sortedJobs = useMemo(() => {
//     return Array.isArray(jobs)
//       ? [...jobs].sort((a, b) => {
//         const order = { Running: 1, Pending: 2, Successful: 3, Stopped: 4, Disabled: 5 };
//         return (order[a.state] || 99) - (order[b.state] || 99);
//       })
//       : [];
//   }, [jobs]);

//   const parseCreationTime = (creationTime) => {
//     if (!creationTime) return null;
//     const parts = creationTime.split(" ");
//     if (parts.length < 2) return null;
//     return parts[1];
//   };

//   const formatTime = (seconds) => {
//     if (seconds <= 0) return null;
//     const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
//     const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
//     const secs = String(seconds % 60).padStart(2, "0");
//     return `${hours}:${minutes}:${secs}`;
//   };

//   // const isJobDisabled = (job) => {
//   //   if (job.info !== "Disabled Automatically") return false;
//   //   const jobTime = new Date(job.endTime || job.startTime || job.creationTime);
//   //   const oneMinuteAgo = new Date(Date.now() - 1 * 60 * 1000);
//   //   return jobTime < oneMinuteAgo;
//   // };

//   // We can remove isJobDisabled since we're not using the Disabled section anymore

//   // const groupJobsByDate = (jobs) => {
//   //   return jobs.reduce((groups, job) => {
//   //     if (!job.creationTime || job.creationTime === "N/A") return groups;
//   //     const datePart = job.creationTime.split(" ")[0];
//   //     if (!groups[datePart]) {
//   //       groups[datePart] = [];
//   //     }
//   //     groups[datePart].push(job);
//   //     return groups;
//   //   }, {});
//   // };
//   const groupJobsByDate = (jobs) => {
//     return jobs.reduce((groups, job) => {
//       if (!job.creationTime) return groups;

//       const date = new Date(job.creationTime);
//       const dateKey = date.toISOString().split("T")[0]; // sadece yyyy-mm-dd al
//       if (!groups[dateKey]) {
//         groups[dateKey] = [];
//       }
//       groups[dateKey].push(job);
//       return groups;
//     }, {});
//   };

//   const toggleDate = (date) => {
//     setExpandedDates(prev => ({
//       ...prev,
//       [date]: !prev[date],
//     }));
//   };

//   // Add this effect near the top with other useEffects
//   useEffect(() => {
//     const savedStoppedJobs = localStorage.getItem('stoppedPendingJobs');
//     if (savedStoppedJobs) {
//       setStoppedPendingJobs(new Set(JSON.parse(savedStoppedJobs)));
//     }
//   }, []);

//   // Modify the stopJob function to persist to localStorage
//   const stopJob = useCallback(async (jobId, folderId, updatedJob = null) => {
//     try {
//       const token = localStorage.getItem("userToken");
//       const storedTenant = JSON.parse(localStorage.getItem("selectedTenant"));

//       if (!storedTenant?.orch_tenant_id) {
//         console.error("No organization unit ID found in selected tenant");
//         return;
//       }

//       const response = await axios.post(
//         `${config.API_BASE_URL}/api/triggerMonitoring/jobs/${jobId}/stop`,
//         {
//           folderId,
//           tenantId: storedTenant.orch_tenant_id,
//           jobInfo: "Pending Disabled (Timeout)"  // Add this explicit message
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'X-UIPATH-OrganizationUnitId': storedTenant.orch_tenant_id.toString(),
//             'Content-Type': 'application/json',
//             'selected-tenant': JSON.stringify(storedTenant)
//           },
//         }
//       );

//       if (response.status === 200 || response.status === 204) {
//         console.log("Successfully stopped job:", jobId);
//         setStoppedPendingJobs(prev => {
//           const newSet = new Set([...prev, jobId]);
//           localStorage.setItem('stoppedPendingJobs', JSON.stringify([...newSet]));
//           return newSet;
//         });
//         await fetchJobs();
//         return;
//       }
//     } catch (error) {
//       console.error("Stop job error:", {
//         status: error.response?.status,
//         data: error.response?.data,
//         jobId: jobId,
//         folderId: folderId
//       });

//       // Add this: Still update UI even if API call fails
//       if (error.response?.status === 404) {
//         console.log("Job already stopped, updating UI...");
//         setStoppedPendingJobs(prev => {
//           const newSet = new Set([...prev, jobId]);
//           localStorage.setItem('stoppedPendingJobs', JSON.stringify([...newSet]));
//           return newSet;
//         });
//       }

//       await fetchJobs();
//     }
//   }, [fetchJobs]);

//   // Update the timer effect to pass folderId
//   useEffect(() => {
//     if (!jobs.length) return;

//     const updatePendingTimers = () => {
//       const newTimers = {};
//       const newShowEndingSoon = {};
//       const stoppedJobs = new Set();

//       sortedJobs
//         .filter(job => job.state === "Pending")
//         .forEach(job => {
//           const creationTimeStr = parseCreationTime(job.creationTime);
//           if (!creationTimeStr) return;

//           const [hh, mm, ss] = creationTimeStr.split(":").map(Number);
//           const creationSeconds = hh * 3600 + mm * 60 + ss;

//           const now = new Date();
//           const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

//           const elapsedSeconds = currentSeconds - creationSeconds;
//           const remainingSeconds = Math.max(TOTAL_PENDING_TIME - elapsedSeconds, 0);

//           if (remainingSeconds === 0 && !job.info?.includes("Disabled Automatically")) {
//             if (!stoppedJobs.has(job.id)) {
//               stoppedJobs.add(job.id);
//               // Add info about automatic stopping
//               const updatedJob = { ...job, info: "Pending Disabled (Timeout)" };
//               stopJob(job.id, job.folderId, updatedJob).catch(console.error);
//             }
//           }

//           newTimers[job.id] = remainingSeconds;
//           if (remainingSeconds <= 60 && remainingSeconds > 0) {
//             newShowEndingSoon[job.id] = true;
//           }
//         });

//       // Only update state if values have changed
//       const currentTimersStr = JSON.stringify(pendingTimers);
//       const newTimersStr = JSON.stringify(newTimers);
//       if (currentTimersStr !== newTimersStr) {
//         setPendingTimers(newTimers);
//       }

//       const currentEndingSoonStr = JSON.stringify(showEndingSoon);
//       const newEndingSoonStr = JSON.stringify(newShowEndingSoon);
//       if (currentEndingSoonStr !== newEndingSoonStr) {
//         setShowEndingSoon(newShowEndingSoon);
//       }
//     };

//     updatePendingTimers();
//     const interval = setInterval(updatePendingTimers, 5000);
//     return () => clearInterval(interval);
//   }, [jobs, stopJob]);



//   useEffect(() => {
//     const interval = setInterval(() => {
//       setShowEndingSoon(prevState => {
//         const newState = { ...prevState };
//         Object.keys(newState).forEach(jobId => {
//           newState[jobId] = !newState[jobId];
//         });
//         return newState;
//       });
//     }, 2000);

//     return () => clearInterval(interval);
//   }, []);

//   return (
//     <div className="trigger-monitoring-container">
//       <Helmet>
//         <title>Trigger Monitoring - Autonomie</title>
//       </Helmet>

//       <h2>Trigger Monitoring</h2>

//       {loading ? (
//         <p>Loading...</p>
//       ) : (
//         <table className="trigger-monitoring-table">
//           <thead>
//             <tr>
//               <th>Process</th>
//               <th>Folder</th>
//               <th>Hostname</th>
//               <th>State</th>
//               <th>Creation Time</th>
//               <th>Started Time</th>
//               <th>Ended Time</th>
//               <th>Duration</th>
//               <th>INFO</th>
//             </tr>
//           </thead>
//           <tbody>
//             {/* Running and Pending Jobs */}
//             {sortedJobs.some(job => job.state === "Running" || job.state === "Pending") ? (
//               sortedJobs
//                 .filter(job => job.state === "Running" || job.state === "Pending")
//                 .sort((a, b) => new Date(b.creationTime) - new Date(a.creationTime)) // en yeni yukarıda
//                 .map(job => (
//                   <tr key={job.id}>
//                     <td>{job.process}</td>
//                     <td>{job.folderName}</td>
//                     <td>{job.hostname || "Not Available"}</td>
//                     <td className={`job-status ${job.state === "Running" ? "flashing-running" : job.state === "Pending" ? "flashing-pending" : ""}`}>
//                       {job.state}
//                     </td>
//                     <td>
//                       {job.creationTime
//                         ? new Date(job.creationTime).toLocaleString("tr-TR", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: false
//                         })
//                         : "Pending"}
//                     </td>

//                     <td>
//                       {job.startTime
//                         ? new Date(job.startTime).toLocaleString("tr-TR", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: false
//                         })
//                         : (job.state === "Pending" ? "Not Started" : "In Progress")}
//                     </td>

//                     <td>
//                       {job.endTime
//                         ? new Date(job.endTime).toLocaleString("tr-TR", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: false
//                         })
//                         : (job.state === "Running" ? "In Progress"
//                           : job.state === "Pending" ? "Not Completed"
//                             : "Not Available")}
//                     </td>

//                     <td>{job.duration || (job.state === "Running" ? "Calculating..." : "Not Available")}</td>
//                     <td>
//                       {job.state === "Pending" ? (
//                         pendingTimers[job.id] === 0 ? (
//                           <span className="disabled-automatically">Disabled Automatically</span>
//                         ) : (
//                           <span className="pending-timer">
//                             {pendingTimers[job.id] <= 60 && showEndingSoon[job.id] ?
//                               "Ending Soon" :
//                               formatTime(pendingTimers[job.id])
//                             }
//                           </span>
//                         )
//                       ) : (
//                         job.info || "No Info"
//                       )}
//                     </td>
//                   </tr>
//                 ))
//             ) : (
//               <tr>
//                 <td colSpan="9" className="no-jobs-message">No running or pending state jobs</td>
//               </tr>
//             )}

//             {/* Successful Jobs */}
//             {sortedJobs.some(job => job.state === "Successful") && (
//               <>
//                 <tr className="collapsible-header" onClick={() => setShowSuccessful(!showSuccessful)}>
//                   <td colSpan="9">🔵 Successful ({sortedJobs.filter(job => job.state === "Successful").length}) {showSuccessful ? "▼" : "▶"}</td>
//                 </tr>
//                 {showSuccessful &&
//                   Object.entries(groupJobsByDate(sortedJobs.filter(job => job.state === "Successful")))
//                   .sort((a, b) => new Date(b[0]) - new Date(a[0]))  // En yeni en üste
//                   .map(([date, jobs]) => (
//                     <React.Fragment key={date}>
//                       <tr className="sub-collapsible-header" onClick={() => toggleDate(date)}>
//                         <td colSpan="9">
//                           📅 {new Date(date).toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
//                           {expandedDates[date] ? " ▼" : " ▶"}
//                         </td>
//                       </tr>

//                       {expandedDates[date] && jobs.map(job => (
//                         <tr key={job.id} className="sub-collapsible-job">
//                           <td>{job.process}</td>
//                           <td>{job.folderName}</td>
//                           <td>{job.hostname || "Not Available"}</td>
//                           <td className={job.state.toLowerCase()}>{job.state}</td>
//                           <td>{job.creationTime ? new Date(job.creationTime).toLocaleString("tr-TR") : "Pending"}</td>

// <td>
//   {job.startTime
//     ? new Date(job.startTime).toLocaleString("tr-TR")
//     : (job.state === "Pending" ? "Not Started" : "In Progress")}
// </td>

// <td>
//   {job.endTime
//     ? new Date(job.endTime).toLocaleString("tr-TR")
//     : (job.state === "Running" ? "In Progress"
//       : job.state === "Pending" ? "Not Completed"
//       : "Not Available")}
// </td>

//                           <td>{job.duration || "Not Available"}</td>
//                           <td>{job.info || "No Info"}</td>
//                         </tr>
//                       ))}
//                     </React.Fragment>
//                   ))
//                 }
//               </>
//             )}

//             {/* Stopped Jobs */}
//             {sortedJobs.some(job => job.state === "Stopped") && (
//               <>
//                 <tr className="collapsible-header stopped" onClick={() => setShowStopped(!showStopped)}>
//                   <td colSpan="9">🔴 Stopped ({sortedJobs.filter(job => job.state === "Stopped").length}) {showStopped ? "▼" : "▶"}</td>
//                 </tr>
//                 {showStopped &&
//                   Object.entries(groupJobsByDate(sortedJobs.filter(job => job.state === "Stopped")))
//                   .sort((a, b) => new Date(b[0]) - new Date(a[0]))  // En yeni en üste
//                   .map(([date, jobs]) => (
//                     <React.Fragment key={date}>
//                       <tr className="sub-collapsible-header" onClick={() => toggleDate(date)}>
//                         <td colSpan="9">
//                           📅 {new Date(date).toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
//                           {expandedDates[date] ? " ▼" : " ▶"}
//                         </td>
//                       </tr>

//                       {expandedDates[date] && jobs.map(job => (
//                         <tr key={job.id} className="sub-collapsible-job">
//                           <td>{job.process}</td>
//                           <td>{job.folderName}</td>
//                           <td>{job.hostname || "Not Available"}</td>
//                           <td className={job.state.toLowerCase()}>{job.state}</td>
//                           <td>{job.creationTime ? new Date(job.creationTime).toLocaleString("tr-TR") : "Pending"}</td>

// <td>
//   {job.startTime
//     ? new Date(job.startTime).toLocaleString("tr-TR")
//     : (job.state === "Pending" ? "Not Started" : "In Progress")}
// </td>

// <td>
//   {job.endTime
//     ? new Date(job.endTime).toLocaleString("tr-TR")
//     : (job.state === "Running" ? "In Progress"
//       : job.state === "Pending" ? "Not Completed"
//       : "Not Available")}
// </td>

//                           <td>{job.duration || "Not Available"}</td>
//                           <td>{stoppedPendingJobs.has(job.id) ? "Pending Disabled (Timeout)" : (job.info || "No Info")}</td>
//                         </tr>
//                       ))}
//                     </React.Fragment>
//                   ))
//                 }
//               </>
//             )}


//           </tbody>
//         </table>
//       )}
//     </div>
//   );
// };

// export default TriggerMonitoring;
